import React, { useState } from 'react';
import {
  Button,
  ContextMenu,
  ContextMenuAction,
  ContextMenuContent,
  RichText,
} from '@wix/wix-base-ui';
import { translate } from '#packages/i18n';
import * as baseUI from '#packages/baseUI';
import { WithRestrictionsRender } from '#packages/editorRestrictions';
import { pagesPanelUpdateMenuMessageDisplaySrc38Evid4 } from '@wix/bi-logger-components-data/v2';
import { TRANSLATIONS_MAP } from '../utils/translations';
import { AUTOMATION_IDS } from '../utils/automationIds';

import type { MenuAddItemAction } from '../menuActions';
import { MenuDeprecationNotificationBanner, constants } from '#packages/menu';
import experiments from 'experiment';
import { isResponsiveEditor } from '@wix/santa-editor-utils';
import type { MenuItem } from '@wix/document-services-types';
import { biLogger } from '#packages/util';

interface MobileDisclaimerFooterProps {
  openMobileDisclaimerArticle: () => void;
}

export const MobileDisclaimerFooter: React.FC<MobileDisclaimerFooterProps> = ({
  openMobileDisclaimerArticle,
}) => {
  return (
    <footer
      className="menu-manage-panel__footer menu-manage-panel__footer_disclaimer"
      data-aid={AUTOMATION_IDS.FOOTER.CONTAINER}
    >
      <RichText type="T02" className="light">
        <p>
          {`${translate(
            TRANSLATIONS_MAP.MAIN_MENU_FLOW.FOOTER.MOBILE_DISCLAIMER,
          )} `}
          <a onClick={openMobileDisclaimerArticle}>
            {translate(
              TRANSLATIONS_MAP.MAIN_MENU_FLOW.FOOTER.MOBILE_DISCLAIMER_LINK,
            )}
          </a>
        </p>
      </RichText>
    </footer>
  );
};

interface FooterProps {
  addActions: MenuAddItemAction[];
  onMainButtonClick: () => void;
  isOldMenu: boolean;
  openHelpCenter: (article: string) => void;
  menuItems: MenuItem[];
}

const FooterPure: React.FC<FooterProps> = ({
  menuItems,
  addActions,
  onMainButtonClick,
  isOldMenu,
  openHelpCenter,
}) => {
  const [IsContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const hasSubMenuItems = !!menuItems?.some(
    (item: MenuItem) => item.items && item.isVisible && item.items.length > 0,
  );
  const isOldMenuWithSubItems =
    hasSubMenuItems && isOldMenu && !isResponsiveEditor();

  const { PANEL_TYPES } = constants;

  // any positive numeric value will fill the full width
  const maxWidth = 1;

  return (
    <footer
      className="menu-manage-panel__footer"
      data-aid={AUTOMATION_IDS.FOOTER.CONTAINER}
    >
      <WithRestrictionsRender name={'menu-panel_add-item'}>
        {({ disabled }) => (
          <ContextMenu
            onToggle={(isOpen: boolean) => {
              if (isOpen && isOldMenuWithSubItems) {
                biLogger.report(
                  pagesPanelUpdateMenuMessageDisplaySrc38Evid4({
                    panelType: PANEL_TYPES.MANAGE_PANEL,
                  }),
                );
              }
              setIsContextMenuOpen(isOpen);
            }}
            isOpen={IsContextMenuOpen}
            disabled={disabled}
            alignment="RIGHT"
            automationId={AUTOMATION_IDS.FOOTER.ADD_PAGE_CONTEXT_MENU}
            dataHook={AUTOMATION_IDS.FOOTER.ADD_PAGE_CONTEXT_MENU}
            maxWidth={maxWidth}
            customButton={
              // for ContextMenuTestkit
              // customButton element should always have a key!!! Otherwise onToggle will be called (wix base ui bug)
              <div data-hook="context-menu-button" key="contextBtn">
                <Button
                  disabled={disabled}
                  onClick={onMainButtonClick}
                  className="add-page-button"
                  automationId={AUTOMATION_IDS.FOOTER.ADD_PAGE_BUTTON}
                >
                  <div className="add-page-button-content">
                    <span className="add-page-button-title">
                      {translate(TRANSLATIONS_MAP.FOOTER.ADD_MENU_ITEM)}
                    </span>
                  </div>
                </Button>
              </div>
            }
          >
            <ContextMenuContent>
              {addActions.map((action) => (
                <ContextMenuAction
                  key={action.symbolName}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  dataHook={action.automationId}
                  automationId={action.automationId}
                  className="sortable-list-item__context-menu-action"
                >
                  <baseUI.SymbolWrapper
                    className="symbol sortable-list-item__context-menu-action-icon"
                    name={action.symbolName}
                  />
                  <span className="sortable-list-item__context-menu-action-text">
                    {action.title}
                  </span>
                </ContextMenuAction>
              ))}
            </ContextMenuContent>
            {experiments.isOpen('se_openMenuDeprecationNotificationBanner') &&
              isOldMenuWithSubItems && (
                <div className="menu-deprecation-notification-banner-manage-panel">
                  <MenuDeprecationNotificationBanner
                    setIsContextMenuOpen={setIsContextMenuOpen}
                    openHelpCenter={openHelpCenter}
                    menuItems={menuItems}
                    panelType={PANEL_TYPES.MANAGE_PANEL}
                  />
                </div>
              )}
          </ContextMenu>
        )}
      </WithRestrictionsRender>
    </footer>
  );
};

const shouldFooterRerender = (
  prevProps: FooterProps,
  nextProps: FooterProps,
) => {
  //Check if notification banner needed.
  if (!nextProps.isOldMenu || isResponsiveEditor()) {
    return false;
  }

  const oldHasSubMenuItems = !!prevProps.menuItems?.some(
    (item: MenuItem) => item.items && item.isVisible && item.items.length > 0,
  );
  const newHasSubMenuItems = !!nextProps.menuItems?.some(
    (item: MenuItem) => item.items && item.isVisible && item.items.length > 0,
  );
  //The hierarchy changed - need re-render
  return oldHasSubMenuItems === newHasSubMenuItems;
};

export const Footer = React.memo(FooterPure, shouldFooterRerender);
